// Referral (?ref=) capture for the invite-a-friend program.
//
// A landing on any page with ?ref={code} stores the code in a first-party
// cookie so it survives the browse → sign-up gap, then rides to the backend
// inside ConversionAttribution (email-code verify) or the OAuth begin params.
// Deliberately NOT part of the PostHog campaign context: analytics capture is
// gated on PostHog being configured, and referral attribution must not be.
//
// Every function here is best-effort and never throws — referral is optional
// enrichment on auth flows.

const REFERRAL_COOKIE = 'soku_ref'
const REFERRAL_TTL_SECONDS = 30 * 24 * 60 * 60 // 30 days
// Kept in sync with the backend validator (apps/api/schemas/auth_self.py).
const REFERRAL_CODE_RE = /^[a-z0-9]{4,16}$/
// Platforms that tag outbound links with ?ref=<platform> (Product Hunt's
// "Visit website" appends ?ref=producthunt). Not invite codes — capturing one
// would overwrite a real friend's code in the cookie. lib/analytics/track.ts
// reads these as an acquisition source instead.
const PLATFORM_REF_TAGS = new Set(['producthunt'])

function normalizeCode(value: string | null | undefined): string | undefined {
  const candidate = value?.trim().toLowerCase()
  return candidate && REFERRAL_CODE_RE.test(candidate) ? candidate : undefined
}

/**
 * Store a valid ?ref= landing param in the referral cookie (last click wins —
 * the backend's first-bind-wins rule is the durable arbiter). Safe to call on
 * every page view.
 */
export function captureReferralParam(): void {
  if (typeof window === 'undefined') return
  try {
    const code = normalizeCode(
      new URLSearchParams(window.location.search).get('ref'),
    )
    if (!code || PLATFORM_REF_TAGS.has(code)) return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${REFERRAL_COOKIE}=${code}; Max-Age=${REFERRAL_TTL_SECONDS}; Path=/; SameSite=Lax${secure}`
  } catch {
    // Cookies can be blocked by privacy settings; referral must never affect
    // page behavior.
  }
}

/** The stored referral code, if a valid one was captured. */
export function getReferralCode(): string | undefined {
  if (typeof document === 'undefined') return undefined
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${REFERRAL_COOKIE}=([^;]*)`),
    )
    return normalizeCode(match ? decodeURIComponent(match[1]) : undefined)
  } catch {
    return undefined
  }
}
